import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Stack, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { currency } from '../../../common/utils/currency';
import AlChip from '../../../common/components/AlChip';
import { PromoCode } from './PromoCode';
import { getSpecialTicketChips } from '../BookingCategoryList/getSpecialTicketChips';

const REDEEM_ELIGIBLE_TIERS = ['Silber', 'Gold'];

const TotalPrice = ({ price, serviceTypeId, onChange, loyalty, redeem, companyId, agencyId }) => {
    const { t } = useTranslation();

    const handlePromoCodeApply = useCallback((promoCodeId) => {
        onChange({ name: 'promoCodeId', value: promoCodeId });
    }, []);

    if (!price) {
        return null;
    }

    const total = price.totalPrice;
    const netAmount = currency(price.netto);
    const vat = Math.round(((price.totalPrice - price.netto) / price.netto) * 100);
    const secondTransfer = price.transfers[1];

    const eur = (cents) => cents / 100;
    const creditCents = loyalty?.currentCreditAmountCents ?? 0;
    // Corporate bookings are outside the program (SoT §6) — no earn, no redeem.
    // This mirrors the backend gate (`isCorporateBooking` in
    // loyalty-redemption-service.js, called from resolveLoyaltyRedemption) on exactly
    // the two fields the payload carries: `booking.company.id` becomes the outgoing
    // `companyId` (bookingClean.js) and `booking.agencyId` is stamped from the
    // logged-in profile (bookingReducer.js). Derived on every render, so picking or
    // clearing a company mid-checkout takes effect immediately.
    const isCorporateBooking = !!companyId || !!agencyId;
    // Auto-calculable == a price object exists (same signal as autoCalculation:
    // Boolean(booking.price), BookingPaymentForm.jsx). Eligibility also requires a
    // current Silber/Gold tier and a positive credit balance.
    const redeemEligible =
        !isCorporateBooking && !!loyalty && creditCents > 0 && REDEEM_ELIGIBLE_TIERS.includes(loyalty.tier.name);
    const redeemedEur = redeemEligible && redeem ? Math.min(eur(creditCents), total) : 0;
    const reducedTotal = Math.max(0, total - redeemedEur);

    const isDiscountAvailable = price.baseMasterPrice > price.basePrice;
    // Whether the Bonusguthaben-reduced total row renders below — exactly the condition
    // that gates that block further down. When it renders, it (not the Gesamt row above)
    // is the final amount the customer pays, so the highlight must move there.
    const showReduced = redeem && redeemedEur > 0;

    const discountData = price.transfers.find((transfer) => transfer.promoCodeData)?.promoCodeData;

    const items = [...price.transfers].map((transfer, i) => {
        const isTransferDiscountAvailable = transfer.baseMasterPrice > transfer.basePrice;

        return (
            <>
                <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {t('booking.transfer')} {secondTransfer ? i + 1 : ''}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body1">{t('booking.price.basePrice')}</Typography>
                    <Stack sx={{ flexDirection: 'row' }}>
                        {isTransferDiscountAvailable && (
                            <Typography sx={{ mr: 1.5 }} variant="body1">
                                <del>{currency(transfer.baseMasterPrice)}</del>
                            </Typography>
                        )}
                        <Typography sx={{ fontWeight: isTransferDiscountAvailable ? 600 : 400 }} variant="body1">
                            {currency(transfer.basePrice)}
                        </Typography>
                    </Stack>
                </Box>

                {['distancePrice', 'luggagePrice', 'servicePrice', 'paymentSystemFee'].map(
                    (key) =>
                        // show luggage label always to clarify that some luggage is free
                        ((key === 'luggagePrice' || transfer[key] !== 0) && (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body1">{t(`booking.price.${key}`)}</Typography>
                                <Typography variant="body1">{currency(transfer[key])}</Typography>
                            </Box>
                        )) ||
                        null,
                )}
            </>
        );
    });
    const specialTicketChips = getSpecialTicketChips(price.servicePackCodes);

    return (
        <Paper sx={{ flex: 1, px: 2.5, pt: 2, pb: 2 }}>
            <Typography variant="h5" sx={{ textAlign: 'center' }}>
                {t('total-calculation')}
            </Typography>
            {specialTicketChips.map((chip) => (
                <AlChip
                    key={chip.servicePackCode}
                    sx={{ mt: 1.25, mr: 1 }}
                    color={chip.color}
                    backgroundColor={chip.backgroundColor}
                    label={t(chip.label)}
                    icon={chip.icon}
                />
            ))}
            {items.map((item, i) => (
                <Fragment key={i}>{item}</Fragment>
            ))}
            <Divider sx={{ mt: 2, mb: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('total')}</Typography>
                <Typography
                    sx={{ fontWeight: isDiscountAvailable && !showReduced ? 500 : 400 }}
                    color={isDiscountAvailable && !showReduced ? 'secondary' : 'inherit'}
                    variant="h6"
                >
                    {currency(total)}
                </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block' }}>
                {t('priceVat', { vat, netAmount })}
            </Typography>
            {redeemEligible && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!redeem}
                            onChange={(e) => onChange({ name: 'redeemLoyaltyCredit', value: e.target.checked })}
                        />
                    }
                    label={t('loyalty.redeem.label', {
                        amount: currency(eur(creditCents)),
                        status: t(`loyalty.tier.${loyalty.tier.name}`),
                    })}
                />
            )}
            {redeem && redeemedEur > 0 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1">{t('loyalty.redeem.deduction')}</Typography>
                        <Typography variant="body1">-{currency(redeemedEur)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">{t('loyalty.redeem.reducedTotal')}</Typography>
                        <Typography sx={{ fontWeight: 500 }} color="secondary" variant="h6">
                            {currency(reducedTotal)}
                        </Typography>
                    </Box>
                </>
            )}
            {/* B2B partner pricing never combines with promo codes (spec §4.5): no input at all */}
            {!price.isPartnerPricing && (
                <PromoCode
                    hasTickets={!!specialTicketChips.length}
                    serviceTypeId={serviceTypeId}
                    code={discountData?.code}
                    discountPercentage={discountData?.discountPercentage}
                    onApply={handlePromoCodeApply}
                />
            )}
        </Paper>
    );
};

export { TotalPrice };
